/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import { withIntlProvider } from '../../../services';
import type { MenuItem, NamedItems } from '../../../types';
import MenuContentItem from '../MenuItem';
import MenuSectionPrimitive from '../MenuSectionPrimitive';
import { getFoldableThreshold } from '../MenuSectionPrimitive/utils';

const LEGACY_MAX_ITEMS = 10;
const OVERFLOW_MAX_ITEMS = 50;

export type NativeMenuSectionProps = {
	namedItems: NamedItems;
	nativeSectionCount: number;
	sectionIndex: number;
	sectionStartIndex: number;
	totalMenuItemsCount: number;
};

function NativeMenuSection({
	namedItems,
	sectionIndex,
	totalMenuItemsCount,
	sectionStartIndex,
	nativeSectionCount,
}: NativeMenuSectionProps) {
	const [showAll, setShowAll] = useState(false);
	const {
		fireNewNativeMenuItemClicked,
		fireNewNativeMenuItemClickedOld,
		fireShowMoreButtonClicked,
	} = useUniversalCreateAnalytics();
	const { product } = useMenuControl();

	const sectionItemsCount = namedItems.items.length;

	const foldableThreshold = getFoldableThreshold(namedItems.foldable);

	const items = useMemo(() => {
		if (!sectionItemsCount) {
			return [];
		}

		if (!namedItems.foldable || sectionItemsCount <= foldableThreshold) {
			return namedItems.items;
		}

		const sectionItemsLimit = fg('navx_universal_create_overflow_mode')
			? OVERFLOW_MAX_ITEMS
			: LEGACY_MAX_ITEMS;

		return namedItems.items.slice(0, showAll ? sectionItemsLimit : foldableThreshold);
	}, [namedItems, showAll, sectionItemsCount, foldableThreshold]);

	const toggleShowMore = useCallback(() => {
		if (typeof namedItems.foldable === 'number' && fg('navx-2610-uc-refactor-show-more')) {
			setShowAll(true); // removing show less functionality under this fg
			fireShowMoreButtonClicked({
				groupHeading: namedItems.heading || 'unknown',
				groupIndex: sectionIndex,
				groupItemCount: sectionItemsCount,
				inMoreItemAtisList: namedItems.items
					.slice(foldableThreshold)
					.map((item) => item.ati.toString()),
				inMoreItemCount: sectionItemsCount - foldableThreshold,
			});
		} else {
			setShowAll((prev) => !prev);
		}
	}, [
		setShowAll,
		namedItems,
		sectionIndex,
		sectionItemsCount,
		fireShowMoreButtonClicked,
		foldableThreshold,
	]);

	const menuItemWithAnalytics = useCallback(
		(item: MenuItem, itemIndex: number): MenuItem => {
			const fireAnalytics = (isRightClick: boolean) => {
				const itemAti = item.ati.toString();
				if (fg('navx-2236-unified-instrumentation-updates')) {
					fireNewNativeMenuItemClicked({
						nativeGroupCount: nativeSectionCount,
						nativeItemCount: totalMenuItemsCount,
						nativeItemIndex: sectionStartIndex + itemIndex,
						groupItemCount: sectionItemsCount,
						groupItemIndex: itemIndex,
						groupItemAti: itemAti,
						groupItemProduct: product,
						isInMore: false,
						isRightClick,
					});
				} else {
					fireNewNativeMenuItemClickedOld({
						ati: itemAti,
						menuItemsCount: totalMenuItemsCount, // Total number of items in the native sections part of menu
						menuItemIndex: sectionStartIndex + itemIndex, // Position of the item in the native sections part of menu
						groupItemProduct: product,
						groupItemsCount: sectionItemsCount, // Number of items in the section
						groupItemsIndex: itemIndex, // Position of the item in the section
						isRightClick,
					});
				}
			};

			return {
				...item,
				onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
					fireAnalytics(false);
					item.onClick?.(event);
				},
				onContextMenu: (event: React.MouseEvent) => {
					fireAnalytics(true);
					item.onContextMenu?.(event);
				},
			};
		},
		[
			fireNewNativeMenuItemClickedOld,
			fireNewNativeMenuItemClicked,
			totalMenuItemsCount,
			sectionStartIndex,
			sectionItemsCount,
			product,
			nativeSectionCount,
		],
	);

	if (!namedItems?.items?.length) {
		return null;
	}

	return (
		<MenuSectionPrimitive
			sectionIndex={sectionIndex}
			itemLength={namedItems.items.length}
			foldable={namedItems.foldable}
			heading={namedItems.heading}
			showAll={showAll}
			toggleShowMore={toggleShowMore}
			isNative
		>
			{items.map((menuItem: MenuItem, index: number) => (
				<MenuContentItem
					item={menuItemWithAnalytics(menuItem, index)}
					key={`menu-section-${sectionIndex}-item-${index}`}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

export default withIntlProvider(NativeMenuSection);
